import { API_BASE_URL } from '../../config/api'
import React, { useState, useEffect, useMemo } from 'react'
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { X, CreditCard, Lock, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react'

interface PaymentModalProps {
  isOpen: boolean
  onClose: () => void
  bookingData: any
  onPaymentSuccess: (paymentResult: any) => void
  onPaymentError: (error: string) => void
  stripePublishableKey?: string
}

interface PaymentFormProps {
  bookingData: any
  onPaymentSuccess: (paymentResult: any) => void
  onPaymentError: (error: string) => void
  onClose: () => void
}

// Stripe card element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      padding: '10px',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: false,
}

function PaymentForm({ bookingData, onPaymentSuccess, onPaymentError, onClose }: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  
  const [processing, setProcessing] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'external'>('stripe')
  const [clientSecret, setClientSecret] = useState('')
  const [externalPaymentUrl, setExternalPaymentUrl] = useState('')
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      city: '',
      country: 'US',
      postal_code: ''
    }
  })

  useEffect(() => {
    // Create payment intent when modal opens
    createPaymentIntent()
  }, [bookingData])

  const createPaymentIntent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_id: bookingData.bookingId,
          tenant_id: bookingData.tenantId,
          amount: bookingData.totalPrice,
          currency: bookingData.currency || 'USD',
          metadata: {
            item_type: bookingData.itemType,
            item_name: bookingData.itemName,
            guest_name: bookingData.guestName || billingDetails.name,
            guest_email: bookingData.guestEmail || billingDetails.email
          }
        }),
      })

      const result = await response.json()

      if (result.success) {
        if (result.requires_redirect) {
          setPaymentMethod('external')
          setExternalPaymentUrl(result.redirect_url)
        } else {
          setPaymentMethod('stripe')
          setClientSecret(result.client_secret)
        }
      } else {
        onPaymentError(result.error === 'No payment gateway configured'
          ? 'Payment is not configured for this operator. Please contact the operator directly.'
          : result.error || 'Failed to initialize payment')
      }
    } catch (error) {
      console.error('Payment intent creation failed:', error)
      onPaymentError('Unable to connect to the payment service. Please try again later.')
    }
  }

  const handleStripePayment = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements || !clientSecret) {
      return
    }

    setProcessing(true)

    const cardElement = elements.getElement(CardElement)

    if (!cardElement) {
      onPaymentError('Card element not found')
      setProcessing(false)
      return
    }

    // Confirm payment with Stripe
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: billingDetails.name,
          email: billingDetails.email,
          address: billingDetails.address
        }
      }
    })

    setProcessing(false)

    if (error) {
      console.error('Payment failed:', error)
      let userMessage: string
      switch (error.type) {
        case 'card_error':
          userMessage = error.message || 'Your card was declined. Please try a different card.'
          break
        case 'validation_error':
          userMessage = 'Please check your card details and try again.'
          break
        default:
          userMessage = 'An error occurred while processing your payment. Please try again.'
      }
      onPaymentError(userMessage)
    } else if (paymentIntent?.status === 'succeeded') {
      // Update booking status
      await updateBookingStatus(paymentIntent.id, 'paid')
      onPaymentSuccess({
        payment_intent_id: paymentIntent.id,
        amount: paymentIntent.amount / 100,
        currency: paymentIntent.currency,
        status: 'succeeded'
      })
    }
  }

  const handleExternalPayment = () => {
    // Open external payment gateway in new window
    const paymentWindow = window.open(
      externalPaymentUrl,
      'payment',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    )

    // Listen for payment completion
    const checkPaymentStatus = setInterval(async () => {
      if (paymentWindow?.closed) {
        clearInterval(checkPaymentStatus)
        // Check payment status
        await checkExternalPaymentStatus()
      }
    }, 1000)
  }

  const checkExternalPaymentStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/status/${bookingData.bookingId}`)
      const result = await response.json()

      if (result.success && result.status === 'succeeded') {
        onPaymentSuccess(result)
      } else if (result.status === 'failed') {
        onPaymentError('Payment was not completed successfully')
      }
    } catch (error) {
      console.error('Failed to check payment status:', error)
      onPaymentError('Failed to verify payment status')
    }
  }

  const updateBookingStatus = async (paymentIntentId: string, status: string) => {
    try {
      await fetch(`${API_BASE_URL}/api/bookings/update-payment-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_id: bookingData.bookingId,
          payment_intent_id: paymentIntentId,
          payment_status: status
        }),
      })
    } catch (error) {
      console.error('Failed to update booking status:', error)
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  return (
    <div className="space-y-6">
      {/* Payment Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Payment Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{bookingData.itemName}</span>
            <span className="font-medium">{formatCurrency(bookingData.totalPrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Guests: {bookingData.guests?.adults || 0} adults, {bookingData.guests?.children || 0} children</span>
          </div>
          {bookingData.nights && (
            <div className="flex justify-between">
              <span className="text-gray-600">{bookingData.nights} nights</span>
            </div>
          )}
          <div className="border-t pt-2 flex justify-between font-semibold">
            <span>Total</span>
            <span>{formatCurrency(bookingData.totalPrice)}</span>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      {paymentMethod === 'stripe' && clientSecret && (
        <form onSubmit={handleStripePayment} className="space-y-4">
          {/* Billing Details */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Billing Information
            </h4>
            
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="Full Name"
                value={billingDetails.name}
                onChange={(e) => setBillingDetails(prev => ({ ...prev, name: e.target.value }))}
                required
              />
              <Input
                type="email"
                placeholder="Email Address"
                value={billingDetails.email}
                onChange={(e) => setBillingDetails(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>

            <Input
              type="text"
              placeholder="Address Line 1"
              value={billingDetails.address.line1}
              onChange={(e) => setBillingDetails(prev => ({
                ...prev,
                address: { ...prev.address, line1: e.target.value }
              }))}
              required
            />

            <div className="grid grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="City"
                value={billingDetails.address.city}
                onChange={(e) => setBillingDetails(prev => ({
                  ...prev,
                  address: { ...prev.address, city: e.target.value }
                }))}
                required
              />
              <Input
                type="text"
                placeholder="Postal Code"
                value={billingDetails.address.postal_code}
                onChange={(e) => setBillingDetails(prev => ({
                  ...prev,
                  address: { ...prev.address, postal_code: e.target.value }
                }))}
                required
              />
            </div>
          </div>

          {/* Card Details */}
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Card Details
            </h4>
            <div className="border rounded-lg p-3 bg-white">
              <CardElement options={cardElementOptions} />
            </div>
          </div>

          {/* Security Notice */}
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-green-50 p-3 rounded-lg">
            <Lock className="w-4 h-4 text-green-600" />
            <span>Your payment information is encrypted and secure. We use Stripe for payment processing.</span>
          </div>

          {/* Pay Button */}
          <Button
            type="submit"
            disabled={!stripe || processing}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 text-lg font-semibold"
          >
            {processing ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing Payment...
              </div>
            ) : (
              `Pay ${formatCurrency(bookingData.totalPrice)}`
            )}
          </Button>
        </form>
      )}

      {/* External Payment Gateway */}
      {paymentMethod === 'external' && externalPaymentUrl && (
        <div className="space-y-4">
          <div className="text-center p-6 bg-blue-50 rounded-lg">
            <ExternalLink className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h4 className="font-semibold text-gray-900 mb-2">
              Complete Payment
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              You will be redirected to our secure payment partner to complete your booking payment.
            </p>
            <Button
              onClick={handleExternalPayment}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
            >
              Continue to Payment
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function PaymentModal({ isOpen, onClose, bookingData, onPaymentSuccess, onPaymentError, stripePublishableKey }: PaymentModalProps) {
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  const [paymentResult, setPaymentResult] = useState<any>(null)

  // Dynamic Stripe key: per-operator key -> env var fallback -> null
  const stripeKey = stripePublishableKey?.trim() || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.trim() || null
  const isValidStripeKey = !!stripeKey && stripeKey.startsWith('pk_')
  const stripePromise = useMemo(
    () => isValidStripeKey ? loadStripe(stripeKey!) : null,
    [stripeKey, isValidStripeKey]
  )

  const handlePaymentSuccess = (result: any) => {
    setPaymentResult(result)
    setPaymentCompleted(true)
    onPaymentSuccess(result)
  }

  const handleClose = () => {
    setPaymentCompleted(false)
    setPaymentResult(null)
    onClose()
  }

  if (!isOpen) return null

  const elementsOptions: StripeElementsOptions = {
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#9333ea',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#dc2626',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
    },
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {paymentCompleted ? 'Payment Successful!' : 'Complete Payment'}
          </h2>
          <Button variant="ghost" size="sm" onClick={handleClose} className="w-8 h-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {paymentCompleted ? (
          /* Payment Success */
          <div className="text-center space-y-4">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Payment Completed Successfully!</h3>
              <p className="text-gray-600">Your booking has been confirmed.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-left">
              <h4 className="font-medium text-green-900 mb-2">Transaction Details</h4>
              <div className="text-sm text-green-800 space-y-1">
                <div className="flex justify-between">
                  <span>Amount:</span>
                  <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: paymentResult?.currency || 'USD' }).format(paymentResult?.amount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Transaction ID:</span>
                  <span className="font-mono text-xs">{paymentResult?.payment_intent_id}</span>
                </div>
              </div>
            </div>
            <Button onClick={handleClose} className="w-full bg-green-600 hover:bg-green-700 text-white">
              Close
            </Button>
          </div>
        ) : !stripePromise ? (
          /* Stripe not configured -- show informational message */
          <div className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Payment Not Available
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Online payment is not currently configured for this operator.
              Please contact the operator directly to complete your booking.
            </p>
            <Button onClick={handleClose} className="w-full bg-gray-600 hover:bg-gray-700 text-white">
              Close
            </Button>
          </div>
        ) : (
          /* Payment Form */
          <Elements stripe={stripePromise} options={elementsOptions}>
            <PaymentForm
              bookingData={bookingData}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={onPaymentError}
              onClose={handleClose}
            />
          </Elements>
        )}
      </div>
    </div>
  )
}

export default PaymentModal